import React, { useState, useEffect } from 'react';
import { Container, Navbar, Nav, Dropdown, Offcanvas } from 'react-bootstrap';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Default to collapsed
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Handle window resize to detect mobile vs desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      if (!mobile && sidebarOpen) {
        setSidebarOpen(false); // Close mobile sidebar when switching to desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
  };

  const getMenuItems = () => {
    // For admin users, show only admin menu items
    if (user?.role === 'admin') {
      return [
        {
          title: 'Admin Dashboard',
          icon: 'fas fa-shield-alt',
          path: '/app/admin/analytics',
          active: location.pathname === '/app/admin/analytics'
        },
        {
          title: 'User Management',
          icon: 'fas fa-users',
          path: '/app/admin/users',
          active: location.pathname.includes('/admin/users')
        },
        {
          title: 'Profile Management',
          icon: 'fas fa-user-edit',
          path: '/app/admin/profiles',
          active: location.pathname.includes('/admin/profiles')
        },
        {
          title: 'Upgrade Requests',
          icon: 'fas fa-rocket',
          path: '/app/admin/upgrades',
          active: location.pathname.includes('/admin/upgrades')
        },
        {
          title: 'Referral Management',
          icon: 'fas fa-sitemap',
          path: '/app/admin/referrals',
          active: location.pathname.includes('/admin/referrals')
        },
        {
          title: 'Task Management',
          icon: 'fas fa-clipboard-list',
          path: '/app/admin/tasks',
          active: location.pathname === '/app/admin/tasks'
        },
        {
          title: 'Task Review Dashboard',
          icon: 'fas fa-clipboard-check',
          path: '/app/admin/task-reviews',
          active: location.pathname.includes('/admin/task-reviews')
        },
        {
          title: 'Reward Management',
          icon: 'fas fa-gift',
          path: '/app/admin/rewards',
          active: location.pathname.includes('/admin/rewards')
        },
        {
          title: 'Transaction Management',
          icon: 'fas fa-exchange-alt',
          path: '/app/admin/transactions',
          active: location.pathname.includes('/admin/transactions')
        },
        {
          title: 'Withdrawal Management',
          icon: 'fas fa-money-bill-wave',
          path: '/app/admin/withdrawals',
          active: location.pathname.includes('/admin/withdrawals')
        },
        {
          title: 'System Settings',
          icon: 'fas fa-cog',
          path: '/app/admin/settings',
          active: location.pathname.includes('/admin/settings')
        }
      ];
    }

    // For regular users, show user menu items
    return [
      {
        title: 'Dashboard',
        icon: 'fas fa-tachometer-alt',
        path: '/app/dashboard',
        active: location.pathname === '/app/dashboard'
      },
      {
        title: 'Tasks',
        icon: 'fas fa-tasks',
        path: '/app/tasks',
        active: location.pathname.includes('/tasks') && !location.pathname.includes('/admin')
      },
      {
        title: 'Transaction History',
        icon: 'fas fa-history',
        path: '/app/transactions',
        active: location.pathname === '/app/transactions'
      },
      {
        title: 'Profile',
        icon: 'fas fa-user',
        path: '/app/profile',
        active: location.pathname === '/app/profile'
      },
      {
        title: 'Upgrade to Executive',
        icon: 'fas fa-rocket',
        path: '/app/upgrade',
        active: location.pathname === '/app/upgrade'
      },
      {
        title: 'Referrals',
        icon: 'fas fa-share-alt',
        path: '/app/referrals',
        active: location.pathname.includes('/referrals')
      },
      {
        title: 'Withdrawals',
        icon: 'fas fa-money-bill-wave',
        path: '/app/withdrawals',
        active: location.pathname.includes('/withdrawals')
      }
    ];
  };

  const menuItems = getMenuItems();

  return (
    <div className="admin-wrapper">


      {/* Top Navigation */}
      <Navbar bg="white" className="admin-navbar shadow-sm" fixed="top">
        <Container fluid>
          <div className="d-flex align-items-center">
            <button
              className="btn btn-primary admin-sidebar-toggle me-3 d-flex align-items-center justify-content-center"
              onClick={() => {
                if (isMobile) {
                  console.log('Mobile mode: toggling sidebar open to:', !sidebarOpen);
                  setSidebarOpen(!sidebarOpen);
                } else {
                  console.log('Desktop mode: toggling sidebar collapsed to:', !sidebarCollapsed);
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
              title={isMobile ? "Toggle Menu" : (sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar")}
              style={{
                width: '44px',
                height: '44px',
                border: '2px solid #007bff',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                position: 'relative',
                zIndex: 1055,
                minWidth: '44px'
              }}
            >
              <i className={`fas ${isMobile ? 'fa-bars' : (sidebarCollapsed ? 'fa-chevron-right' : 'fa-bars')}`}></i>
            </button>
            <Navbar.Brand className="admin-brand">
              <i className="fas fa-network-wired text-primary me-2"></i>
              <strong>NetworqPay</strong>
              {sidebarCollapsed && (
                <small className="text-muted ms-2">
                  <i className="fas fa-compress-alt" title="Sidebar is collapsed"></i>
                </small>
              )}
            </Navbar.Brand>
          </div>

          <Nav className="ms-auto">
            {/* User Balance */}
            <div className="d-flex align-items-center me-3">
              <div className="text-center">
                <div className="small text-muted">Balance</div>
                <div className="fw-bold text-success">₹{user?.rewardBalance?.toFixed(2) || '0.00'}</div>
              </div>
            </div>

            {/* Notifications - Temporarily hidden */}
            {/* TODO: Uncomment when notifications feature is ready
            <Dropdown align="end">
              <Dropdown.Toggle variant="link" className="nav-link p-2 position-relative">
                <i className="fas fa-bell text-muted"></i>
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger small">
                  3
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="shadow-lg border-0">
                <Dropdown.Header>Notifications</Dropdown.Header>
                <Dropdown.Item>
                  <i className="fas fa-task text-success me-2"></i>
                  New task available
                </Dropdown.Item>
                <Dropdown.Item>
                  <i className="fas fa-coins text-warning me-2"></i>
                  Reward credited
                </Dropdown.Item>
                <Dropdown.Item>
                  <i className="fas fa-user-plus text-info me-2"></i>
                  Referral joined
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            */}

            {/* User Menu */}
            <Dropdown align="end">
              <Dropdown.Toggle 
                variant="link" 
                className="nav-link p-2 d-flex align-items-center text-decoration-none border-0"
                style={{ background: 'none' }}
              >
                <div className="admin-avatar me-2">
                  <i className={user?.role === 'admin' ? 'fas fa-user-shield' : 'fas fa-user'}></i>
                </div>
                <div className="d-none d-md-flex flex-column text-start">
                  <span className="text-dark fw-semibold mb-0" style={{ fontSize: '0.9rem', lineHeight: '1.1' }}>
                    {user?.name}
                  </span>
                  <span className={`badge ${user?.role === 'admin' ? 'bg-danger' : 'bg-primary'} align-self-start`} 
                        style={{ fontSize: '0.7rem' }}>
                    {user?.role?.toUpperCase()}
                  </span>
                </div>
                <i className="fas fa-chevron-down ms-2 text-muted" style={{ fontSize: '0.7rem' }}></i>
              </Dropdown.Toggle>
              <Dropdown.Menu className="shadow-lg border-0" style={{ minWidth: '250px' }}>
                <Dropdown.Header className="bg-light">
                  <div className="d-flex align-items-center">
                    <div className="admin-avatar me-3" style={{ width: '40px', height: '40px' }}>
                      <i className={user?.role === 'admin' ? 'fas fa-user-shield' : 'fas fa-user'}></i>
                    </div>
                    <div>
                      <strong className="d-block">{user?.name}</strong>
                      <small className="text-muted d-block">{user?.email}</small>
                      <span className={`badge ${user?.role === 'admin' ? 'bg-danger' : 'bg-primary'} mt-1`}>
                        {user?.role?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </Dropdown.Header>
                <Dropdown.Divider />
                <Dropdown.Item as={Link} to="/app/profile" className="py-2">
                  <i className="fas fa-user me-2 text-primary"></i>
                  Profile Settings
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/app/help" className="py-2">
                  <i className="fas fa-question-circle me-2 text-info"></i>
                  Help & Support
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleLogout} className="text-danger py-2">
                  <i className="fas fa-sign-out-alt me-2"></i>
                  <strong>Logout</strong>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </Container>
      </Navbar>

      {/* Sidebar for larger screens */}
      
      {/* <div className={`admin-sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}> */}
      <div className={`admin-sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
        <div className="admin-sidebar-header">
          <button
              className="btn admin-sidebar-toggle p-0 me-3"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <i className="fas fa-bars text-dark"></i>
            </button>
          <div className="admin-user-panel">
            <div className="admin-user-avatar">
              <i className={user?.role === 'admin' ? 'fas fa-user-shield' : 'fas fa-user'}></i>
            </div>
            <div className="admin-user-info">
              <div className="admin-user-name">{user?.name}</div>
              <div className="admin-user-role">{user?.role === 'admin' ? 'Administrator' : 'User'}</div>
            </div>
          </div>
        </div>

        <div className="admin-sidebar-menu">
          {menuItems.map((item, index) => {
            if (item.title.startsWith('---') && item.title.endsWith('---')) {
              return (
                <div key={index} className="admin-menu-header">
                  {item.title.replace(/---/g, '').trim()}
                </div>
              );
            }
            return (
              <Link
                key={index}
                to={item.path}
                className={`admin-menu-item ${item.active ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <i className={item.icon}></i>
                <span>{item.title}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Mobile Sidebar */}
      <Offcanvas 
        show={sidebarOpen} 
        onHide={() => {
          console.log('Offcanvas closing');
          setSidebarOpen(false);
        }} 
        placement="start"
        style={{ zIndex: 1060 }}
        className="d-lg-none"
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>
            <i className="fas fa-network-wired text-primary me-2"></i>
            NetworqPay
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="admin-user-panel mb-4">
            <div className="admin-user-avatar">
              <i className={user?.role === 'admin' ? 'fas fa-user-shield' : 'fas fa-user'}></i>
            </div>
            <div className="admin-user-info">
              <div className="admin-user-name">{user?.name}</div>
              <div className="admin-user-role">{user?.role === 'admin' ? 'Administrator' : 'User'}</div>
              {/* Balance temporarily hidden */}
              {/* <div className="text-success small">Balance: ${user?.rewardBalance?.toFixed(2) || '0.00'}</div> */}
            </div>
          </div>

          <div className="admin-sidebar-menu">
            {menuItems.map((item, index) => {
              if (item.title.startsWith('---') && item.title.endsWith('---')) {
                return (
                  <div key={index} className="admin-menu-header">
                    {item.title.replace(/---/g, '').trim()}
                  </div>
                );
              }
              return (
                <Link
                  key={index}
                  to={item.path}
                  className={`admin-menu-item ${item.active ? 'active' : ''}`}
                  onClick={() => {
                    console.log('Menu item clicked, closing mobile sidebar');
                    setSidebarOpen(false);
                  }}
                >
                  <i className={item.icon}></i>
                  <span>{item.title}</span>
                </Link>
              );
            })}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Sidebar Overlay */}
      {sidebarOpen && <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      {/* Main Content */}
      <div className={`admin-content ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Container fluid>
          <Outlet />
        </Container>
      </div>
    </div>
  );
};

export default Layout;